import { useState } from 'react';
import { Plus, GitBranch, Grid, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeveloperPageLayout } from '@/components/developer/shared/DeveloperPageLayout';
import { BotTechnicalCard } from '@/components/developer/BotTechnicalCard';
import { BotTechnicalTable } from '@/components/developer/BotTechnicalTable';
import { BotDetailPanel } from '@/components/developer/BotDetailPanel';

// Mock bot data
const mockBots = [
  {
    id: 'bot-1',
    name: 'Lead Qualification Bot',
    status: 'healthy',
    uptime: 99.8,
    memory: 324,
    cpu: 45,
    requests: 12847,
    version: 'v2.1.3',
    lastDeploy: '2024-01-15T10:30:00Z',
    environment: 'production'
  },
  {
    id: 'bot-2',
    name: 'Customer Support Bot',
    status: 'warning',
    uptime: 94.2,
    memory: 567,
    cpu: 78,
    requests: 8932,
    version: 'v1.8.1',
    lastDeploy: '2024-01-14T14:22:00Z',
    environment: 'production'
  },
  {
    id: 'bot-3',
    name: 'Sales Demo Bot',
    status: 'healthy',
    uptime: 100,
    memory: 198,
    cpu: 23,
    requests: 4521,
    version: 'v3.0.0-beta',
    lastDeploy: '2024-01-16T09:15:00Z',
    environment: 'staging'
  }
];

// Mock hook for bot data
const useBots = () => ({
  bots: mockBots,
  loading: false,
  error: null
});

interface ButtonGroupProps {
  children: React.ReactNode;
}

function ButtonGroup({ children }: ButtonGroupProps) {
  return (
    <div className="flex rounded-md border">
      {children}
    </div>
  );
}

export function DeveloperBots() {
  const { bots } = useBots();
  const [selectedBot, setSelectedBot] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  
  const openDeployModal = () => {
    console.log('Opening deploy modal...');
  };

  const syncWithGit = () => {
    console.log('Syncing with Git...');
  };

  return (
    <DeveloperPageLayout
      title="Bot Management"
      subtitle={`${bots.length} bots activos`}
      quickActions={[
        {
          icon: <Plus className="w-4 h-4" />,
          label: "Deploy Bot",
          onClick: openDeployModal,
          shortcut: "Ctrl+D"
        },
        {
          icon: <GitBranch className="w-4 h-4" />,
          label: "Git Sync",
          onClick: syncWithGit,
          shortcut: "Ctrl+G"
        }
      ]}
      actions={
        <ButtonGroup>
          <Button
            size="sm"
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            onClick={() => setViewMode('grid')}
            className="rounded-r-none border-r-0"
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'outline'}
            onClick={() => setViewMode('table')}
            className="rounded-l-none"
          >
            <List className="w-4 h-4" />
          </Button>
        </ButtonGroup>
      }
    >
      {/* Bot Grid/Table View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bots.map((bot) => (
            <BotTechnicalCard
              key={bot.id}
              bot={bot}
              onSelect={() => setSelectedBot(bot.id)}
              selected={selectedBot === bot.id}
            />
          ))}
        </div>
      ) : (
        <BotTechnicalTable
          bots={bots}
          onSelect={setSelectedBot}
          selectedId={selectedBot}
        />
      )}

      {/* Bot Detail Panel */}
      {selectedBot && (
        <BotDetailPanel
          botId={selectedBot}
          onClose={() => setSelectedBot(null)}
        />
      )}
    </DeveloperPageLayout>
  );
}